import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import invoiceService from '../services/invoiceService';
import '../styles/invoices.css';

const money = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const date = (value) => {
  if (!value) return '—';

  const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`);

  return Number.isNaN(parsed.getTime())
    ? '—'
    : parsed.toLocaleDateString('en-IN');
};

// Format quantity without accidentally displaying a price or stock value
// as the unit.
const formatQuantity = (value) => {
  const quantity = Number(value);

  if (!Number.isFinite(quantity)) return '0';

  return quantity.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
};

// Display a unit only when it is a recognizable text unit.
// Numeric values such as "100" should not appear as the unit.
const formatUnit = (value) => {
  if (value === null || value === undefined) return '';

  const unit = String(value).trim();

  if (!unit || Number.isFinite(Number(unit))) return '';

  return unit;
};

const InvoiceDetailsPage = () => {
  const { invoiceNumber } = useParams();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const loadInvoice = useCallback(async () => {
    setLoading(true);
    setError('');
    setInvoice(null);

    try {
      const result = await invoiceService.getInvoiceByNumber(invoiceNumber);
      const invoiceData = result?.invoice || result;

      if (!invoiceData || !invoiceData.invoice_number) {
        setInvoice(null);
        setError('Invoice not found.');
        return;
      }

      setInvoice({
        ...invoiceData,
        items: Array.isArray(invoiceData.items)
          ? invoiceData.items
          : [],
      });
    } catch (err) {
      setInvoice(null);
      setError(
        err.response?.data?.message || 'Unable to load invoice.'
      );
    } finally {
      setLoading(false);
    }
  }, [invoiceNumber]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  const handleDownload = async () => {
    if (downloading || !invoiceNumber) return;

    setDownloading(true);
    setError('');

    try {
      await invoiceService.downloadInvoicePdf(invoiceNumber);
    } catch (err) {
      setError(
        err.response?.data?.message || 'Unable to download invoice PDF.'
      );
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <section className="invoices-page">
        <div className="invoice-form-card">Loading invoice...</div>
      </section>
    );
  }

  if (!invoice) {
    return (
      <section className="invoices-page">
        <div
          className="invoice-alert invoice-alert-error"
          role="alert"
        >
          {error || 'Invoice not found.'}
        </div>

        <Link
          to="/user/invoices"
          className="invoice-secondary-button"
        >
          Back to invoices
        </Link>
      </section>
    );
  }

  return (
    <section className="invoices-page">
      <header className="invoices-page-header">
        <div>
          <Link to="/user/invoices" className="invoice-back-link">
            ← Back to invoices
          </Link>

          <h1>Invoice {invoice.invoice_number}</h1>

          <p>Review billing details and invoice line items.</p>
        </div>

        <button
          type="button"
          className="invoice-primary-button"
          onClick={handleDownload}
          disabled={downloading}
        >
          {downloading ? 'Preparing PDF...' : 'Download PDF'}
        </button>
      </header>

      {error && (
        <div
          className="invoice-alert invoice-alert-error"
          role="alert"
        >
          {error}
        </div>
      )}

      <article className="invoice-detail-card">
        <div className="invoice-detail-top">
          <div>
            <span className="invoice-detail-label">
              Invoice number
            </span>

            <h2>{invoice.invoice_number}</h2>

            <span
              className={`invoice-status invoice-status-${invoice.status || 'draft'}`}
            >
              {invoice.status || 'draft'}
            </span>
          </div>

          <div className="invoice-detail-dates">
            <div>
              <span>Invoice date</span>
              <strong>{date(invoice.invoice_date)}</strong>
            </div>

            <div>
              <span>Due date</span>
              <strong>{date(invoice.due_date)}</strong>
            </div>
          </div>
        </div>

        <div className="invoice-detail-parties">
          <section>
            <h3>From — Firm</h3>

            <strong>{invoice.firm_name || '—'}</strong>

            <p>{invoice.firm_address || 'Address not provided'}</p>

            {invoice.firm_gst_number && (
              <p>GSTIN: {invoice.firm_gst_number}</p>
            )}

            {invoice.firm_pan_number && (
              <p>PAN: {invoice.firm_pan_number}</p>
            )}

            {invoice.firm_bank_name && (
              <p>Bank: {invoice.firm_bank_name}</p>
            )}

            {invoice.firm_account_number && (
              <p>Account: {invoice.firm_account_number}</p>
            )}

            {invoice.firm_ifsc_code && (
              <p>IFSC: {invoice.firm_ifsc_code}</p>
            )}
          </section>

          <section>
            <h3>Bill To — Customer</h3>

            <strong>{invoice.dealer_name || '—'}</strong>

            <p>{invoice.dealer_address || 'Address not provided'}</p>

            {invoice.dealer_mobile && (
              <p>Mobile: {invoice.dealer_mobile}</p>
            )}

            {invoice.dealer_gst_number && (
              <p>GSTIN: {invoice.dealer_gst_number}</p>
            )}

            {invoice.dealer_pan_number && (
              <p>PAN: {invoice.dealer_pan_number}</p>
            )}
          </section>
        </div>

        <div className="invoice-detail-items">
          <h3>Invoice Items</h3>

          <div className="invoice-detail-table-wrapper">
            <table className="invoice-detail-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Batch</th>
                  <th>HSN</th>
                  <th>Qty</th>
                  <th>Unit Price</th>
                  <th>Discount</th>
                  <th>GST</th>
                  <th>Line Total</th>
                </tr>
              </thead>

              <tbody>
                {invoice.items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.product_name || '—'}</strong>

                      {item.sku && (
                        <span className="invoice-cell-secondary">
                          {item.sku}
                        </span>
                      )}
                    </td>

                    <td>{item.batch_number || '—'}</td>
                    <td>{item.hsn_code || '—'}</td>

                    <td>
                      {formatQuantity(item.quantity)}
                      {formatUnit(item.unit)
                        ? ` ${formatUnit(item.unit)}`
                        : ''}
                    </td>

                    <td>{money(item.unit_price)}</td>
                    <td>{money(item.discount_amount)}</td>
                    <td>{Number(item.gst_rate || 0)}%</td>
                    <td>{money(item.line_total)}</td>
                  </tr>
                ))}

                {!invoice.items.length && (
                  <tr>
                    <td colSpan="8">
                      No invoice items were found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="invoice-detail-bottom">
          <div className="invoice-detail-notes">
            <h3>Notes</h3>

            <p>{invoice.notes || 'No additional notes.'}</p>

            <p>
              Supply type:{' '}
              {invoice.supply_type === 'inter_state'
                ? 'Inter-state'
                : invoice.supply_type === 'intra_state'
                  ? 'Intra-state'
                  : '—'}
            </p>
          </div>

          <div className="invoice-detail-totals">
            <div>
              <span>Subtotal</span>
              <strong>{money(invoice.subtotal)}</strong>
            </div>

            <div>
              <span>Discount</span>
              <strong>{money(invoice.discount_amount)}</strong>
            </div>

            <div>
              <span>Taxable amount</span>
              <strong>{money(invoice.taxable_amount)}</strong>
            </div>

            <div>
              <span>CGST</span>
              <strong>{money(invoice.cgst_amount)}</strong>
            </div>

            <div>
              <span>SGST</span>
              <strong>{money(invoice.sgst_amount)}</strong>
            </div>

            <div>
              <span>IGST</span>
              <strong>{money(invoice.igst_amount)}</strong>
            </div>

            <div>
              <span>Total tax</span>
              <strong>{money(invoice.total_tax)}</strong>
            </div>

            <div>
              <span>Round off</span>
              <strong>{money(invoice.round_off)}</strong>
            </div>

            <div className="invoice-detail-grand-total">
              <span>Grand total</span>
              <strong>{money(invoice.total_amount)}</strong>
            </div>
          </div>
        </div>
      </article>
    </section>
  );
};

export default InvoiceDetailsPage;